import { TodoInputBlock } from '@coast/schemas';
import { useCallback, useMemo } from 'react';
import { useCardFormContext } from '@shared/components/blocks/contexts/form';
import { TodoItem } from '@shared/components/blocks/types';
import { MBTouchableOpacity } from '@shared/components/touchable';
import { CompressedTodoBlock } from './CompressedTodoBlock';
import { MBTodoAddRow } from './add';
import { MBTodoItemRow } from './item';

interface Props {
  block: TodoInputBlock;
  onPress?(): void;
}

export function MBCardInputBlockTodo({ block, onPress }: Props) {
  const mode = block.mode || 'creation';
  const { setFieldValue, handleBlur, submitForm, values } = useCardFormContext();

  const todos: TodoItem[] = values[block.name];
  const onBlur = useMemo(() => handleBlur(block.name), [handleBlur, block.name]);
  const onChange = useCallback(
    (value: TodoItem[]) => {
      setFieldValue(block.name, value);
      if (mode === 'completion') {
        return submitForm();
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [setFieldValue, block],
  );

  const handleAdd = useCallback(
    (addedTodos: TodoItem[]) => {
      onChange([...(todos || []), ...addedTodos]);
    },
    [onChange, todos],
  );
  const handleUpdate = useCallback(
    (updated: TodoItem) => onChange(todos.map(todo => (todo.id === updated.id ? updated : todo))),
    [onChange, todos],
  );
  const handleRemove = useCallback(
    (id: string) => onChange(todos.filter(todo => todo.id !== id)),
    [onChange, todos],
  );

  const content = (
    <>
      {!!block.label && (!!todos?.length || mode === 'creation') && (
        <CompressedTodoBlock block={block} todos={todos} />
      )}
      {todos?.map(todo => (
        <MBTodoItemRow
          key={todo.id}
          todo={todo}
          block={block}
          onRemove={handleRemove}
          onUpdate={handleUpdate}
        />
      ))}
      {mode === 'creation' && <MBTodoAddRow block={block} onAdd={handleAdd} onBlur={onBlur} />}
    </>
  );

  // Note(fant): we can't wrap text areas in disabled MBTouchableOpacity
  // (space/enter won't work for some reason...)
  if (mode === 'creation') {
    return content;
  }
  return (
    <MBTouchableOpacity activeOpacity={1} onPress={onPress}>
      {content}
    </MBTouchableOpacity>
  );
}
